/**
 * Where the dashboard finds its backend, resolved once at module load.
 *
 * Priority, highest first:
 *   1. `window.__FIELDIQ_CONFIG__` — written to `/config.js` by the `apps/web`
 *      container at start-up, so one image serves every environment.
 *   2. `VITE_API_URL` / `VITE_WS_URL` — build-time values (Phase 2B names).
 *   3. `VITE_API_HOST` / `VITE_WS_HOST` — the older build-time names, kept working.
 *   4. `http://localhost:3000` for `pnpm dev` against a local backend.
 *
 * The WebSocket host is derived from the API host (http→ws, https→wss) whenever
 * it is not given explicitly at the same level or above.
 *
 * Mock mode follows the same ladder: a runtime `mockMode` wins over the build-time
 * `VITE_MOCK_MODE`, and both are opt-in — only the literal `true` turns it on.
 * See `mockMode.ts` for why a missing value must never mean "demo".
 */

export interface RuntimeConfig {
  apiUrl?: string | null;
  wsUrl?: string | null;
  mockMode?: string | boolean | null;
}

/** The subset of `import.meta.env` the resolver reads; passed in so tests stay pure. */
export interface BuildEnv {
  VITE_API_URL?: string;
  VITE_WS_URL?: string;
  VITE_API_HOST?: string;
  VITE_WS_HOST?: string;
  VITE_MOCK_MODE?: string;
}

declare global {
  interface Window {
    __FIELDIQ_CONFIG__?: RuntimeConfig;
  }
}

export const DEFAULT_API_HOST = 'http://localhost:3000';

function present(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function stripSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

export function wsFromHttp(apiHost: string): string {
  return apiHost.replace(/^http(s?):\/\//, (_m: string, s: string) => `ws${s}://`);
}

export function resolveHosts(
  runtime: RuntimeConfig | undefined,
  env: BuildEnv,
): { apiHost: string; wsHost: string } {
  const apiHost = stripSlash(
    present(runtime?.apiUrl) ??
      present(env.VITE_API_URL) ??
      present(env.VITE_API_HOST) ??
      DEFAULT_API_HOST,
  );
  const wsHost = stripSlash(
    present(runtime?.wsUrl) ??
      present(env.VITE_WS_URL) ??
      present(env.VITE_WS_HOST) ??
      wsFromHttp(apiHost),
  );
  return { apiHost, wsHost };
}

export function resolveMockMode(runtime: RuntimeConfig | undefined, env: BuildEnv): boolean {
  const fromRuntime = runtime?.mockMode;
  if (fromRuntime === true || fromRuntime === false) return fromRuntime;
  const runtimeText = present(typeof fromRuntime === 'string' ? fromRuntime : undefined);
  if (runtimeText !== undefined) return runtimeText === 'true';
  return env.VITE_MOCK_MODE === 'true';
}

function runtimeConfig(): RuntimeConfig | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__FIELDIQ_CONFIG__;
}

const resolved = resolveHosts(runtimeConfig(), import.meta.env as BuildEnv);

export const apiHost: string = resolved.apiHost;
export const wsHost: string = resolved.wsHost;
export const mockModeRequested: boolean = resolveMockMode(
  runtimeConfig(),
  import.meta.env as BuildEnv,
);
